import { isOfficial } from './geoEvents';
import { quotedHeadline } from './voice';
import { eventNames } from './newsMatch';
import { valuedHoldings } from './portfolioExposures';
import { fmtPct } from './formatters';

// ─── What counts as a change ─────────────────────────────────────────────────
// Everything here is measured against the snapshot taken at the previous
// visit (lib/homeVisit.js) and comes from data the app already holds. No new
// source, no new call, and no judgement: each item is a fact, a number, its
// tie to the portfolio, and where it came from.
//
// ─── The thresholds, and why these ───────────────────────────────────────────
// A threshold is a claim about what is worth a line on a screen, so each one
// is written down rather than tuned by feel (DECISIONS.md, 2026-09-24):
//
//   PRICE_PCT   3%   a holding whose price moved this much since the last
//                    visit. Below it the move is inside an ordinary day and
//                    saying so would be manufacturing a change.
//   WEIGHT_PP   2    percentage points of the portfolio. A weight moves
//                    either because a price moved or because the holder
//                    traded; either way, two points is when the shape of the
//                    portfolio has visibly changed.
//   MAX_ITEMS   5    the screen answers a question, it does not empty a
//                    database onto the page.
//   SMALL_PCT   1    a holding under 1% of the portfolio is listed only when
//   IMPACT_PCT  0.1  its move changed the portfolio's value by at least 0.1%
//                    of it (sprint 16, 4.1). A tenth of a percent is $50 on a
//                    $50,000 portfolio: it scales with the portfolio, where a
//                    fixed dollar sum would be noise on a large one and a wall
//                    on a small one. Under 1%, that takes a move of 10% or
//                    more (at 0.5% of the portfolio, 20%).
//
// Price moves are ranked by what they did to the portfolio in dollars
// (shares × the price change since the visit), not by their percent: 5% on
// half the portfolio comes before 20% on a sliver of it (sprint 16, 4.1).
//
// Order is by kind, not by size: something that HAPPENED (a report, a
// government document) outranks something that MOVED, which outranks a count
// of what was published. Within a kind, the larger number first.
export const PRICE_PCT = 3;
export const WEIGHT_PP = 2;
export const MAX_ITEMS = 5;
export const SMALL_PCT = 1;
export const IMPACT_PCT = 0.1;

const pct = (from, to) => (from > 0 ? ((to - from) / from) * 100 : 0);

// ─── Is the previous snapshot fit to compare against? ───────────────────────
// A comparison is only as true as the snapshot under it. On 25.09 a phone
// showed "+179.9%" and a weight going from 55.0% to 80.8% · both measured
// against a snapshot that held buy prices recorded before the quotes arrived.
// So before a single price or weight is compared, the snapshot has to pass
// every check below. If one fails, nothing is compared and the screen says
// there is not enough data · it does not guess which half of it was right.
//
//   legacy   · built before the fix (no `v: 2`), may hold placeholder prices
//   prices   · a holding with no price, or a price that is not a number
//   weights  · the weights do not add to 100 (±1)
//   changed  · holdings were added or removed since, so weights are not
//              comparable, and a new holding has nothing to compare to
//   shares   · a holding's share count is not the one in the snapshot (a
//              holding removed and added again). The dollar impact of a move
//              is price change × shares, and with other shares it is not what
//              the move did (sprint 16, review). Snapshots from before this
//              carry no count and are compared as before.
//   jump     · a price moved more than JUMP_PCT between visits. We hold no
//              verified closing price to confirm a move that large, so it is
//              not shown (DECISIONS.md, 2026-09-25)
export const JUMP_PCT = 50;
export const SNAPSHOT_VERSION = 2;

export function checkSnapshot(snapshot, portfolio = []) {
  const held = snapshot?.holdings;
  if (!held || typeof held !== 'object' || !Object.keys(held).length) return { ok: false, reason: 'missing' };
  if (snapshot.v !== SNAPSHOT_VERSION) return { ok: false, reason: 'legacy' };
  const entries = Object.entries(held);
  if (entries.some(([, h]) => !(Number.isFinite(h?.p) && h.p > 0) || !Number.isFinite(h?.w))) return { ok: false, reason: 'prices' };
  const wsum = entries.reduce((a, [, h]) => a + h.w, 0);
  if (Math.abs(wsum - 100) > 1) return { ok: false, reason: 'weights' };
  // A holding with no quote never enters a snapshot, so it is not a change.
  const now = portfolio.filter(p => p.shares > 0 && p.quote !== 'none').map(p => p.symbol).sort().join(',');
  const then = entries.map(([s]) => s).sort().join(',');
  if (now !== then) return { ok: false, reason: 'changed' };
  for (const p of portfolio) {
    if (p.quote !== 'live' || !(p.currentPrice > 0)) continue;
    const h = held[p.symbol];
    if (!h) return { ok: false, reason: 'changed' };
    if (Number.isFinite(h.s) && Math.abs(h.s - p.shares) > 1e-9) return { ok: false, reason: 'shares' };
    if (Math.abs(pct(h.p, p.currentPrice)) > JUMP_PCT) return { ok: false, reason: 'jump' };
  }
  return { ok: true, reason: null };
}

// The sentence the screen shows instead of a comparison.
export const NOT_COMPARABLE = 'אין מספיק נתונים להשוואה מהביקור הקודם';
export const NOT_COMPARABLE_WHY = {
  legacy:  'הצילום הקודם נשמר לפני שכל המחירים הגיעו',
  prices:  'בצילום הקודם חסרים מחירים',
  weights: 'המשקלות בצילום הקודם אינם מסתכמים ל-100%',
  changed: 'התיק השתנה מאז: נוספו או הוסרו החזקות',
  shares:  'כמות המניות בהחזקה השתנתה מאז הביקור הקודם',
  jump:    'שינוי מחיר של יותר מ-50% בין הביקורים, ואין מחיר סגירה מאומת לאשר אותו',
  missing: 'אין צילום מהביקור הקודם',
};

// ─── The items ───────────────────────────────────────────────────────────────
// { id, kind, rank, symbol, text, figure, tie, source }
//   text   — the fact, in words, with no adjective
//   figure — the number that fact is about, already formatted
//   tie    — how it touches this portfolio (weight, direct/sector)
//   source — who said so
export function computeChanges({ portfolio = [], snapshot = null, geoEvents = [], news = {}, since = null, max = MAX_ITEMS }) {
  const items = [];
  const sinceMs = since ? since.getTime() : 0;
  const prev = snapshot?.holdings || {};
  const prevReports = snapshot?.reports || {};

  // Weights from the same function as "חשיפות התיק" (sprint 14, 3.1). The
  // screen computes this only once every price has arrived (HomeTab,
  // `settled`): before that a pending holding carries its buy price, and
  // AAPL read 55.0% here while every other screen said 82.0%.
  const base = valuedHoldings(portfolio);
  const priced = base.rows;
  const weightBy = new Map(base.rows.map(r => [r.symbol, r.weightPct]));
  const weightOf = p => weightBy.get(p.symbol) || 0;

  // 1 · a report that was scheduled and whose date has passed. The earnings
  //     feed only looks forward, so "it was published" is answered from what
  //     we knew last time rather than from a feed we do not have.
  const todayISO = new Date().toISOString().slice(0, 10);
  for (const [symbol, date] of Object.entries(snapshot?.v === SNAPSHOT_VERSION ? prevReports : {})) {
    if (!date || date > todayISO) continue;
    if (!portfolio.some(p => p.symbol === symbol)) continue;
    const h = portfolio.find(p => p.symbol === symbol);
    items.push({
      id: `report:${symbol}:${date}`, kind: 'report', rank: 0, symbol,
      text: 'הדוח הרבעוני פורסם', figure: date,
      tie: { symbol, weight: h ? weightOf(h) : null, relation: 'direct' },
      source: 'לוח הדוחות של Finnhub',
    });
  }

  // 2 · official documents and geopolitical events that name a holding, filed
  //     since the last visit. A document outranks an article about one.
  const seen = new Set();
  for (const ev of geoEvents) {
    if (!ev || !ev.category || ev.category === 'none') continue;
    const at = new Date(ev.published_at || ev.created_at).getTime();
    if (!(at >= sinceMs)) continue;
    // The headline has to name the holding (utils/newsMatch.js · eventNames).
    const hit = portfolio.find(p => eventNames(ev, p.symbol, p.name && p.name !== p.symbol ? p.name : ''));
    if (!hit || seen.has(hit.symbol)) continue;
    seen.add(hit.symbol);
    const official = isOfficial(ev);
    items.push({
      id: `geo:${ev.id}`, kind: official ? 'official' : 'geo', rank: official ? 1 : 2, symbol: hit.symbol,
      text: official ? 'פורסם מסמך רשמי שנוגע להחזקה' : 'אירוע גיאופוליטי שנוגע להחזקה',
      figure: null,
      tie: { symbol: hit.symbol, weight: weightOf(hit), relation: 'direct' },
      source: ev.source_name || (official ? 'Federal Register' : 'geo_events'),
      // The same quotation the geo screen and its reader show.
      headline: quotedHeadline(ev)?.text || null,
      at: ev.published_at || ev.created_at || null,
      url: ev.source_url || null,
    });
  }

  // Prices and weights are compared only once every holding has a real quote
  // (a pending holding carries its buy price as a placeholder) AND the
  // snapshot passed checkSnapshot. Otherwise nothing is compared.
  const settled = !portfolio.some(p => p.quote === 'pending');
  const fit = checkSnapshot(snapshot, portfolio).ok;
  const live = settled && fit ? priced.filter(p => p.quote === 'live') : [];

  // 3 · price moves since the snapshot. With no snapshot there is nothing to
  //     compare against and nothing is claimed.
  for (const p of live) {
    const before = prev[p.symbol];
    if (!before || !(before.p > 0)) continue;
    const move = pct(before.p, p.currentPrice);
    if (Math.abs(move) < PRICE_PCT) continue;
    // What the move did to the portfolio, in dollars, at today's share count.
    const impact = (p.currentPrice - before.p) * p.shares;
    if (weightOf(p) < SMALL_PCT && !(base.total > 0 && Math.abs(impact) >= (IMPACT_PCT / 100) * base.total)) continue;
    items.push({
      id: `price:${p.symbol}`, kind: 'price', rank: 3, symbol: p.symbol,
      text: move > 0 ? 'המחיר עלה מאז הביקור הקודם' : 'המחיר ירד מאז הביקור הקודם',
      // A change, so two places, as every change in the app (sprint 17).
      figure: fmtPct(move),
      move,
      impact,
      size: Math.abs(impact),
      tie: { symbol: p.symbol, weight: weightOf(p), relation: 'direct' },
      source: 'Finnhub',
    });
  }

  // 4 · weight changes. A holding can move without its price moving much —
  //     everything else moved instead — and that is a different fact.
  for (const p of live) {
    const before = prev[p.symbol];
    if (!before || before.w == null) continue;
    const now = weightOf(p);
    const dpp = now - before.w;
    if (Math.abs(dpp) < WEIGHT_PP) continue;
    items.push({
      id: `weight:${p.symbol}`, kind: 'weight', rank: 4, symbol: p.symbol,
      text: 'חלקה בתיק השתנה',
      // In words, not an arrow: an arrow between two numbers points one way in
      // an LTR run and the other in RTL text, and "31.0% ← 35.3%" read as the
      // wrong direction on the phone (25.09). `figureRtl` tells the screen to
      // lay this one out right-to-left.
      figure: `מ-${before.w.toFixed(1)}% ל-${now.toFixed(1)}%`,
      figureRtl: true,
      from: before.w, to: now,
      move: dpp,
      tie: { symbol: p.symbol, weight: now, relation: 'direct' },
      source: 'מחושב מההחזקות ומהמחירים',
    });
  }

  // 5 · how much was published about a holding since the last visit. A count,
  //     not a headline: the articles themselves are one tap away.
  //     One row per LEAD article, not per company: the same article about
  //     MSFT and AMZN was two rows with one headline (phone check, 25.09).
  //     Holdings whose newest article is the same one share a row, and the
  //     count is of distinct articles across them.
  const groups = new Map();
  for (const [symbol, list] of Object.entries(news)) {
    if (!Array.isArray(list) || !list.length) continue;
    const fresh = sinceMs ? list.filter(a => (a.datetime || 0) * 1000 >= sinceMs) : list;
    if (!fresh.length) continue;
    const h = portfolio.find(p => p.symbol === symbol);
    if (!h) continue;
    const lead = fresh[0];
    const key = lead.id || lead.url || `${symbol}`;
    const g = groups.get(key) || { lead, symbols: [], articles: new Map(), weight: 0 };
    g.symbols.push(symbol);
    g.weight += weightOf(h);
    for (const a of fresh) g.articles.set(a.id || a.url, a);
    groups.set(key, g);
  }
  for (const [key, g] of groups) {
    const n = g.articles.size;
    const many = g.symbols.length > 1;
    items.push({
      id: `news:${g.symbols.join('+')}`, kind: 'news', rank: 5, symbol: g.symbols[0], symbols: g.symbols,
      text: many ? (n === 1 ? 'כתבה חדשה על החברות' : 'כתבות חדשות על החברות') : (n === 1 ? 'כתבה חדשה על החברה' : 'כתבות חדשות על החברה'),
      figure: String(n),
      tie: { symbol: g.symbols[0], symbols: g.symbols, weight: g.weight, relation: 'direct' },
      source: g.lead.source || 'Finnhub',
      headline: g.lead.title || null,
      at: g.lead.datetime || null,
      articleKey: key,
    });
  }

  // Within a kind, the larger first: price moves by their dollar impact,
  // weight changes by their points.
  const sizeOf = i => i.size ?? Math.abs(i.move || 0);
  items.sort((a, b) => (a.rank - b.rank) || (sizeOf(b) - sizeOf(a)));
  return items.slice(0, max);
}

// ─── Days until an event, in words ───────────────────────────────────────────
// Whole days on the calendar, not 24-hour blocks: "tomorrow" is tomorrow's
// date, whatever the time now is.
export function daysUntil(dateISO, now = new Date()) {
  if (!dateISO) return null;
  const a = new Date(`${dateISO}T00:00:00`);
  const b = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (!Number.isFinite(a.getTime())) return null;
  return Math.round((a - b) / 86400000);
}

export function daysUntilText(dateISO, now = new Date()) {
  const d = daysUntil(dateISO, now);
  if (d == null) return null;
  if (d < 0)  return null;
  if (d === 0) return 'היום';
  if (d === 1) return 'מחר';
  return `בעוד ${d} ימים`;
}
